import { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopHeader from './TopHeader';
import { useTheme } from '@/context/ThemeContext';
import { useFormacaoRedirect } from '@/hooks/useFormacaoRedirect';
import { usePageTransition } from '@/hooks/usePageTransition';

export function AppLayout() {
  const { theme } = useTheme();
  const location = useLocation();
  useFormacaoRedirect();
  const { style: transitionStyle } = usePageTransition(200);

  const bgImageStyle = useMemo(() => {
    if (!theme.pageBgImage) return null;
    const fit = theme.pageBgImageFit ?? 'cover';
    const pos = theme.pageBgImagePosition ?? 'center';
    return {
      backgroundImage: `url(${theme.pageBgImage})`,
      backgroundSize: fit === 'repeat' ? 'auto' : fit === 'contain' ? 'contain' : 'cover',
      backgroundRepeat: fit === 'repeat' ? 'repeat' : 'no-repeat',
      backgroundPosition: pos,
      backgroundAttachment: theme.pageBgImageFixed ? 'fixed' : 'local',
    };
  }, [
    theme.pageBgImage,
    theme.pageBgImageFit,
    theme.pageBgImagePosition,
    theme.pageBgImageFixed,
  ]);

  return (
    <>
      <style>{`
        @media print {
          .app-layout-sidebar,
          .app-layout-topheader { display: none !important; }
          .app-layout-root {
            display: block !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }
          .app-layout-right {
            display: block !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
          }
          .app-layout-content {
            display: block !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
          }
        }
      `}</style>
      <div
        className="app-layout-root"
        style={{
          display: 'flex',
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: 'var(--theme-page-bg)',
        }}
      >
        {/* Sidebar esquerda fixa */}
        <div className="app-layout-sidebar" style={{ display: 'flex', flexShrink: 0 }}>
          <Sidebar />
        </div>

        {/* Área direita: topbar + conteúdo */}
        <div
          className="app-layout-right"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          {/* Barra de topo */}
          <div className="app-layout-topheader">
            <TopHeader />
          </div>

          {/* Conteúdo com scroll */}
          <div
            className="app-layout-content"
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              position: 'relative',
            }}
          >
            {/* Conteúdo com transição suave entre páginas */}
            <div
              key={location.pathname}
              style={{
                position: 'relative',
                zIndex: 1,
                minHeight: '100%',
                ...transitionStyle,
              }}
            >
              <Outlet />
            </div>
            {/* Imagem de fundo da área central — sobreposta ao conteúdo */}
            {bgImageStyle && (
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  zIndex: 2,
                  opacity: (theme.pageBgImageOpacity ?? 15) / 100,
                  willChange: 'opacity',
                  ...bgImageStyle,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AppLayout;
